import { apiGet, apiPatch, apiPost } from "@/shared/api";

export type StatoPrenotazione =
  | "in_corso"
  | "prenotato"
  | "rifiutato"
  | "esito_ignoto"
  | "annullato";

/** Stati che occupano il giorno: con uno di questi c'è già un ritiro. */
export const STATI_ATTIVI: StatoPrenotazione[] = ["in_corso", "prenotato", "esito_ignoto"];

export type Prenotazione = {
  id: number;
  tipo: "fornitore" | "reso";
  magazzino: string | null;
  return_id: number | null;
  data_ritiro: string;
  stato: StatoPrenotazione;
  codice: string | null;
  aggiuntiva: boolean;
  colli: number;
  peso_kg: number;
  volume_m3: number;
  voluminoso: boolean;
  errore: string | null;
  creato_da: string;
  created_at: string;
  ordini: string[];
  /** Riferimenti degli ordini che il corriere non ha preso (riprenotati altrove). */
  lasciati: string[];
  /** Tracking del ritiro: ordini già scansionati dal corriere. null = tracking
   *  SDA spento, OM non lo sa (mai «nessuno partito»). */
  partiti: string[] | null;
};

export type OrdineLasciato = { order_id: number; reference: string; colli: number };
/** Ritiro passato in cui il corriere non ha preso tutti gli ordini (o nessuno). */
export type RitiroMancato = {
  prenotazione_id: number;
  data_ritiro: string;
  parziale: boolean;
  totali: number;
  lasciati: OrdineLasciato[];
};

export type Candidato = {
  order_id: number;
  reference: string;
  rif_fornitore: string;
  cliente: string;
  canale: string;
  /** Vuota = LDV non ancora generata: la riga porta il blocco «manca la LDV». */
  ldv: string;
  colli: number;
  peso_kg: number;
  volume_m3: number;
  /** Il collo più grande, in cm: «Prossimo ritiro» mostra misure e voluminoso. */
  max_l: number;
  max_p: number;
  max_a: number;
  richiesta_il: string | null;
  ship_deadline: string | null;
  pronto: boolean;
  blocco: string | null;
};

export type Impostazioni = {
  automatico: boolean;
  ora_automatico: string;
  /** ``""`` = automatico del mattino spento. */
  ora_automatico_mattina: string;
  telefono: string;
  referente: string;
  apertura_mattina_da: string;
  apertura_mattina_a: string;
  apertura_pomeriggio_da: string;
  apertura_pomeriggio_a: string;
  ragione_sociale: string;
  indirizzo: string;
  cap: string;
  localita: string;
  provincia: string;
};

export type MagazzinoRiepilogo = {
  slot: string;
  etichetta: string;
  citta: string;
  automatico_possibile: boolean;
  /** Cosa vuol dire «pronto» per l'automatico; "" = automatico non possibile. */
  segnale_pronto: string;
  indirizzo_modificabile: boolean;
  impostazioni: Impostazioni;
  mancanti: string[];
  da_prenotare: number;
  colli_da_prenotare: number;
  prossimo: Prenotazione | null;
  mancato: RitiroMancato | null;
};

export type SchedaMagazzino = {
  riepilogo: MagazzinoRiepilogo;
  indirizzo: Record<string, string> | null;
  candidati: Candidato[];
  giorni: string[];
  /** «Oggi» si prenota fino a quest'ora (HH:MM): la decide il backend. */
  limite_in_giornata: string;
  prenotazioni: Prenotazione[];
  mancati: RitiroMancato[];
};

export type MailAnnullo = { a: string; oggetto: string; testo: string };
export type GiorniRitiro = { giorni: string[]; limite_in_giornata: string };
/** Esito del ritiro dal cliente mostrato nel dialog del reso. ``daRiprova``:
 *  esito di un RITENTATIVO (tasto «Prenota il ritiro» dopo un fallimento), non
 *  della prenotazione fatta insieme alla creazione — la bozza del messaggio al
 *  cliente è già stata scritta con l'esito del primo tentativo e non si
 *  riscrive da sola: serve avvisare che va aggiornata a mano. */
export type EsitoRitiroUi = { prenotazione?: Prenotazione; errore?: string; daRiprova?: boolean };

/** Voce speciale dell'elenco magazzini: i ritiri a casa dei clienti (resi). */
export const SLOT_RESI = "resi";

/** Un'azione (PATCH, azione di cronologia…) è andata a buon fine, ma il
 *  reload che il chiamante fa subito dopo è fallito: NON è un fallimento
 *  dell'azione stessa. Chi la intercetta non deve tornare al valore/stato
 *  vecchio, solo segnalare che la scheda non si è aggiornata del tutto. */
export class RicaricaFallita extends Error {}

export const fetchGiorni = () => apiGet<GiorniRitiro>("/api/pickups/giorni");
export const fetchMagazzini = () => apiGet<MagazzinoRiepilogo[]>("/api/pickups/magazzini");
export const fetchScheda = (slot: string) =>
  apiGet<SchedaMagazzino>(`/api/pickups/magazzini/${encodeURIComponent(slot)}`);
export const aggiornaMagazzino = (slot: string, patch: Partial<Impostazioni>) =>
  apiPatch<MagazzinoRiepilogo>(`/api/pickups/magazzini/${encodeURIComponent(slot)}`, patch);
/** Ordine trovato dalla ricerca libera: qualsiasi ordine, i filtri della
 *  scheda arrivano come avvisi da leggere, non come blocchi. */
export type OrdineCercato = Candidato & { magazzino: string | null; avvisi: string[] };
export type MisuraManuale = { order_id: number; colli: number; peso_kg: number };

export const cercaOrdini = (slot: string, q: string) =>
  apiGet<OrdineCercato[]>(
    `/api/pickups/magazzini/${encodeURIComponent(slot)}/cerca?${new URLSearchParams({ q }).toString()}`,
  );
export const prenotaMagazzino = (
  slot: string,
  body: {
    data: string;
    order_ids: number[];
    aggiuntiva?: boolean;
    /** Ritiro libero: ordini cercati a mano, fuori dai candidati della scheda. */
    libero?: boolean;
    misure?: MisuraManuale[];
  },
) => apiPost<Prenotazione>(`/api/pickups/magazzini/${encodeURIComponent(slot)}/prenota`, body);
/** Libera gli ordini lasciati dal corriere e li prenota di nuovo. */
export const riprenota = (id: number, body: { data: string; aggiuntiva?: boolean }) =>
  apiPost<Prenotazione>(`/api/pickups/prenotazioni/${id}/riprenota`, body);
export const fetchPrenotazioni = (filtro: { magazzino?: string; tipo?: string }) => {
  const q = new URLSearchParams(
    Object.entries(filtro).filter((e): e is [string, string] => Boolean(e[1])),
  );
  return apiGet<Prenotazione[]>(`/api/pickups/prenotazioni?${q.toString()}`);
};
export const annullaPrenotazione = (id: number) =>
  apiPost<{ prenotazione: Prenotazione; mail: MailAnnullo }>(
    `/api/pickups/prenotazioni/${id}/annulla`,
  );
export const risolviEsito = (id: number, body: { prenotata: boolean; codice?: string }) =>
  apiPost<Prenotazione>(`/api/pickups/prenotazioni/${id}/esito`, body);
/** ``ritiroMancato``: il corriere non è passato al ritiro prenotato. Il backend lo
 *  chiude e ne prenota un altro sulla stessa LDV del reso (niente etichetta nuova). */
export const prenotaRitiroReso = (returnId: number, data: string, ritiroMancato = false) =>
  apiPost<Prenotazione>(`/api/pickups/resi/${returnId}/prenota`, {
    data,
    ritiro_mancato: ritiroMancato,
  });

/** Il ritiro di un reso a cui il corriere può non essere passato: prenotato e col
 *  giorno arrivato o passato. Uno futuro si annulla, un esito ignoto si verifica. */
export function ritiroResoMancabile(p: Prenotazione | null | undefined, oggi: string): boolean {
  return Boolean(p && p.stato === "prenotato" && p.data_ritiro <= oggi);
}
/** L'ultima prenotazione di ogni reso, in qualunque stato (chiave = id del reso). */
export const fetchRitiriResi = (ids: number[]) =>
  apiGet<Record<string, Prenotazione>>(`/api/pickups/resi?ids=${ids.join(",")}`);

/** Il prossimo ritiro SDA prenotato (fornitori), da oggi in poi; null se nessuno.
 *  Le prenotazioni arrivano dalla data più lontana: il prossimo è l'ultimo attivo. */
export async function fetchProssimoRitiro(oggi: string): Promise<Prenotazione | null> {
  const righe = await fetchPrenotazioni({ tipo: "fornitore" });
  const attivi = righe.filter((p) => STATI_ATTIVI.includes(p.stato) && p.data_ritiro >= oggi);
  return attivi.reduce<Prenotazione | null>(
    (min, p) => (min === null || p.data_ritiro < min.data_ritiro ? p : min),
    null,
  );
}

export function mailtoAnnullo(mail: MailAnnullo): string {
  return `mailto:${mail.a}?subject=${encodeURIComponent(mail.oggetto)}&body=${encodeURIComponent(mail.testo)}`;
}

/** Mezzogiorno locale: nessun fuso sposta il giorno. */
export function comeData(iso: string): Date {
  return new Date(`${iso}T12:00:00`);
}

export function etichettaGiorno(iso: string, oggiIso: string): { sopra: string; numero: string } {
  const d = comeData(iso);
  const sopra =
    iso === oggiIso ? "oggi" : d.toLocaleDateString("it-IT", { weekday: "short" }).replace(".", "");
  return { sopra, numero: String(d.getDate()) };
}

export function giornoLungo(iso: string): string {
  return comeData(iso).toLocaleDateString("it-IT", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

/** «25/09»: il giorno di un ritiro dentro una frase. */
export function giornoMese(iso: string): string {
  const [, mm, gg] = iso.split("-");
  return `${gg}/${mm}`;
}

export function oggiIso(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const gg = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${gg}`;
}

/** Il reso parte di norma DOMANI: il cliente deve imballare e farsi trovare.
 *  «Oggi» resta sceglibile finché il backend lo propone (prima delle 11:50). */
export function giornoPredefinitoReso(giorni: string[], oggi: string): string | null {
  return giorni[0] === oggi ? (giorni[1] ?? null) : (giorni[0] ?? null);
}
